import {
    Heart, Search, Bell, MessageSquare, ChevronRight,
    Wind, Droplets, Calendar, Plus,
    Users, BookOpen, Stethoscope, Activity,
    Clock, MessageCircle, ThumbsUp, TrendingUp, AlertTriangle,
    X, Settings
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWeather } from '../hooks/useWeather';
import { LocationButton } from '../components/LocationButton';

// Utility to check if geolocation was already requested
function hasRequestedGeo() {
    return localStorage.getItem('weather_geo_requested') === 'true';
}
import { calendarService } from '../services/calendar';
import { communityService } from '../services/community';
import { consumableTrackerService } from '../services/inhaler';
import { CONSUMABLE_TRACKER_TYPE_LABELS } from '../constants/consumableTrackerTypes';
import { articlesService } from '../services/articles';
import ArticlesPreview from '../components/ArticlesPreview';
import styles from './Dashboard.module.css';
import AIChatWidget from '../components/AIChatWidget';
import { fetchDailySummary } from '../services/dailySummary';

const cardIds = {
    weather: 'weather',
    tasks: 'tasks',
    community: 'community',

    inhaler: 'inhaler',
    health: 'health',

    aiChat: 'aiChat',
    appointments: 'appointments',
    articles: 'articles',

    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6'
};

export default function Dashboard() {

    const [dailySummary, setDailySummary] = useState('');
    const [dailySummaryLoading, setDailySummaryLoading] = useState(false);
    const [dailySummaryError, setDailySummaryError] = useState(null);

    // MOVED UP: Declare weatherData and other hooks BEFORE useEffect that uses them
    const { data: weatherData, loading: weatherLoading, error: weatherError, fetchDefault } = useWeather();
    const [locationRequested, setLocationRequested] = useState(() => hasRequestedGeo());
    const [tasks, setTasks] = useState([]);
    const [communityPosts, setCommunityPosts] = useState([]);
    const [trackerStatus, setTrackerStatus] = useState({
        1: null,
        2: null,
        3: null,
        4: null,
        5: null,
        6: null
    });
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({
        weather: false,
        tasks: false,
        posts: false,
        inhaler: false,
        articles: false
    });
    const [hiddenCards, setHiddenCards] = useState(() => {
        try {
            const stored = localStorage.getItem('dashboard_hidden_cards');
            return stored ? JSON.parse(stored) : {};
        } catch (e) {
            return {};
        }
    });
    const [showCardManager, setShowCardManager] = useState(false);
    const [recentlyClosedCard, setRecentlyClosedCard] = useState(null);
    const [modalJustOpened, setModalJustOpened] = useState(false);
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(orientation: portrait)').matches;

    // Handler for location button click
    const handleLocationClick = async () => {
        setLocationRequested(true);
        localStorage.setItem('weather_geo_requested', 'true');
        await fetchDefault();
    };

    useEffect(() => {
        const fetchOrUseCache = async () => {
            const key = 'dashboard_daily_summary';
            const cached = localStorage.getItem(key);
            let shouldFetch = true;

            // Check if valid cache exists
            if (cached) {
                try {
                    const { text, timestamp } = JSON.parse(cached);
                    if (
                        text &&
                        timestamp &&
                        Date.now() - timestamp < 10 * 60 * 60 * 1000
                    ) {
                        setDailySummary(text);
                        shouldFetch = false;
                    }
                } catch (e) {
                    console.error('Summary cache parse error', e);
                }
            }

            // Fetch if no valid cache
            if (shouldFetch) {
                setDailySummaryLoading(true);
                try {
                    const summary = await fetchDailySummary(weatherData);
                    setDailySummary(summary);
                    localStorage.setItem(key, JSON.stringify({ text: summary, timestamp: Date.now() }));
                    setDailySummaryError(null);
                } catch (e) {
                    console.error('Failed to fetch daily summary:', e);
                    setDailySummaryError('Kunne ikke hente dagens sundhedsopdatering');
                } finally {
                    setDailySummaryLoading(false);
                }
            }
        };

        fetchOrUseCache();
    }, [weatherData]);

    const currentDate = new Date().toLocaleDateString('da-DK', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });

    // Use NODE_ENV to prefer mock data in non-production builds.
    const isDev = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production')
        || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));

    const fetchPosts = async (categoryId = null) => {
        try {
            if (isDev) console.info('Dashboard: dev mode — using community mock data when available');
            return await communityService.getPosts(categoryId);
        } catch (e) {
            console.error('Error fetching posts in Dashboard.fetchPosts:', e);
            return [];
        }
    };

    useEffect(() => {
        loadDashboardData();
    }, [fetchDefault]);

    useEffect(() => {
        if (showCardManager) {
            setModalJustOpened(true);
            const timer = setTimeout(() => setModalJustOpened(false), 300);
            return () => clearTimeout(timer);
        }
    }, [showCardManager]);

    useEffect(() => {
        if (recentlyClosedCard) {
            const timer = setTimeout(() => setRecentlyClosedCard(null), 2000);
            return () => clearTimeout(timer);
        }
    }, [recentlyClosedCard]);

    const loadDashboardData = async () => {
        setLoading(true);
        setErrors({ weather: false, tasks: false, posts: false, inhaler: false, articles: false });

        try {
            let weatherResult = null;

            try {
                weatherResult = await fetchDefault();
            } catch (e) {
                console.error(e);
                setErrors(prev => ({ ...prev, weather: true }));
            }

            const cacheKey = 'dashboard_daily_summary';
            const cached = localStorage.getItem(cacheKey);

            let needsSummaryFetch = true;

            if (cached) {
                try {
                    const { timestamp } = JSON.parse(cached);

                    if (timestamp && Date.now() - timestamp < 10 * 60 * 60 * 1000) {
                        needsSummaryFetch = false;
                    }
                } catch { }
            }

            if (needsSummaryFetch) {
                try {
                    setDailySummaryLoading(true);
                    setDailySummaryError(null);

                    const summary = await fetchDailySummary(weatherResult);

                    setDailySummary(summary);

                    localStorage.setItem(
                        cacheKey,
                        JSON.stringify({
                            text: summary,
                            timestamp: Date.now()
                        })
                    );
                } catch (e) {
                    console.error('summary error', e);
                    setDailySummaryError('Kunne ikke hente dagens sundhedsopdatering');
                } finally {
                    setDailySummaryLoading(false);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleTaskComplete = async (taskId) => {
        try {
            await calendarService.updateTask(taskId, { status: 'DONE' });
            setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'DONE' } : t));
        } catch (e) { console.error(e); }
    };

    const handleTaskSnooze = async (taskId) => {
        try {
            setTasks(prev => prev.map(t => {
                if (t.id === taskId) {
                    // Add 15 minutes to the task's time
                    const oldDate = new Date(t.time || t.startAt || t.date || '');
                    const newDate = new Date(oldDate.getTime() + 15 * 60000);
                    // Persist new time via API
                    calendarService.updateTask(taskId, { status: t.status, time: newDate.toISOString() });
                    return { ...t, time: newDate.toISOString() };
                }
                return t;
            }));
        } catch (e) { console.error(e); }
    };

    const handleTaskCancel = async (taskId) => {
        try {
            await calendarService.updateTask(taskId, { status: 'CANCELLED' });
            setTasks(prev => prev.filter(t => t.id !== taskId));
        } catch (e) { console.error(e); }
    };

    const handleTrackerUse = async (type) => {
        try {
            setTrackerStatus(prev => ({
                ...prev,
                [type]: prev[type] ? { ...prev[type], dosesLeft: Math.max(0, (prev[type].dosesLeft || 0) - 1), dosesToday: (prev[type].dosesToday || 0) + 1 } : null
            }));
            await consumableTrackerService.setAmount(type);
        } catch (e) {
            console.error(e);
            setErrors(prev => ({ ...prev, inhaler: true }));
        }
    };

    const handleNewTracker = async (type) => {
        const label = CONSUMABLE_TRACKER_TYPE_LABELS[type];
        const input = prompt(`Hvor mange doser har din nye ${label}?`);
        if (input === null) return;
        const numDoses = parseInt(input, 10);
        if (isNaN(numDoses) || numDoses < 0) {
            alert('Venligst indtast et gyldigt tal');
            return;
        }
        try {
            setTrackerStatus(prev => ({
                ...prev,
                [type]: prev[type] ? { ...prev[type], dosesLeft: numDoses, dosesToday: 0 } : null
            }));
            await consumableTrackerService.setAmount(type, numDoses);
        } catch (e) {
            console.error(e);
            setErrors(prev => ({ ...prev, inhaler: true }));
        }
    };

    const toggleCardVisibility = (cardId) => {
        // On mobile, show confirmation before hiding
        if (isMobile && !hiddenCards[cardId]) {
            const confirmed = window.confirm('Skjul dette kort? Du kan vise det igen i indstillingerne.');
            if (!confirmed) return;
        }

        setHiddenCards(prev => {
            const updated = { ...prev, [cardId]: !prev[cardId] };
            localStorage.setItem('dashboard_hidden_cards', JSON.stringify(updated));
            // Trigger shimmer animation when card is hidden
            if (updated[cardId]) {
                setRecentlyClosedCard(cardId);
            }
            return updated;
        });
    };

    const isCardHidden = (cardId) => hiddenCards[cardId] === true;
    const hasHiddenCards = Object.values(hiddenCards).some(v => v === true);

    // Removed global loading screen: dashboard always renders, even while loading

    // Extract pollen metrics from API response
    const average_pollen = weatherData?.average_pollen ?? null;
    const alder_pollen = weatherData?.alder_pollen ?? null;
    const birch_pollen = weatherData?.birch_pollen ?? null;
    const grass_pollen = weatherData?.grass_pollen ?? null;
    const mugwort_pollen = weatherData?.mugwort_pollen ?? null;
    const olive_pollen = weatherData?.olive_pollen ?? null;
    const ragweed_pollen = weatherData?.ragweed_pollen ?? null;
    const uv_index = weatherData?.uv_index ?? null;
    const european_aqi = weatherData?.european_aqi ?? null;
    const isStale = weatherData?.note === 'stale';

    const humidity = weatherData?.humidity ?? null;
    // Deprecated: dosesLeft, dosesToday, etc. are now per tracker type and handled in the tracker widgets below.
    // If you need a global healthScore, you can use trackerStatus.inhaler?.healthScore or similar.

    const SmallError = ({ text = 'Server error' }) => (
        <div style={{ padding: '0.5rem', background: 'var(--dashboard-error-bg)', border: '1px solid var(--dashboard-error-border)', borderRadius: 6, color: 'var(--dashboard-error-text)', fontSize: 13, textAlign: 'center' }}>{text}</div>
    );

    // Gradient color functions - smooth transition from green to red
    const getPollenColor = (value) => {
        if (value == null) return '#d1d5db';
        // For severe allergies: ideal 0-10, moderate 10-50, dangerous 50+
        if (value < 10) {
            return '#22c55e'; // Ideal (low pollen)
        } else if (value <= 50) {
            const ratio = (value - 10) / 40;
            return interpolateColor('#22c55e', '#f59e0b', ratio);
        } else {
            return '#dc2626'; // Dangerous (high pollen)
        }
    };

    const getHumidityColor = (value) => {
        if (value == null) return '#d1d5db';
        // Optimal range for respiratory health is 30-50%
        // <30% causes dry air irritation, >50% promotes mold and dust mites
        if (value < 30) {
            // Dry air: green to orange (0-30%)
            const ratio = Math.max(0, (30 - value) / 30);
            return interpolateColor('#22c55e', '#f59e0b', ratio);
        } else if (value > 50) {
            // Too humid: orange to red (50-100%)
            const ratio = Math.min(1, (value - 50) / 50);
            return interpolateColor('#f59e0b', '#dc2626', ratio);
        } else {
            // Perfect range (30-50%)
            return '#22c55e';
        }
    };

    const getUvColor = (value) => {
        if (value == null) return '#d1d5db';
        // 0-3: safe, 3-7: moderate, 7+: high (adjusted for allergy protection)
        if (value < 3) {
            return '#22c55e'; // Safe
        } else if (value <= 7) {
            const ratio = (value - 3) / 4;
            return interpolateColor('#22c55e', '#f59e0b', ratio);
        } else {
            return '#dc2626'; // High exposure
        }
    };

    const getAqiColor = (value) => {
        if (value == null) return '#d1d5db';
        // 0-35: good, 36-75: moderate, 76+: poor
        const bounded = Math.min(Math.max(value, 0), 300);
        const ratio = bounded / 300;
        if (ratio < 0.35) {
            // Green to Orange (0-105)
            return interpolateColor('#31df70', '#f59e0b', ratio / 0.35);
        } else {
            // Orange to Red (105+)
            return interpolateColor('#f59e0b', '#dc2626', (ratio - 0.35) / 0.65);
        }
    };

    const getInhalerColor = (value) => {
        if (value == null) return '#9ca3af';
        // >= 20: safe (gray), < 20: warning (orange), < 10: danger (red)
        if (value >= 20) {
            return '#9ca3af'; // Gray - safe
        } else if (value >= 10) {
            const ratio = (20 - value) / 10; // 10-20 maps to 0-1
            return interpolateColor('#f59e0b', '#f59e0b', ratio); // Orange
        } else {
            return '#dc2626'; // Red - danger
        }
    };

    // Helper function to interpolate between two colors
    const interpolateColor = (color1, color2, factor) => {
        const c1 = parseInt(color1.slice(1), 16);
        const c2 = parseInt(color2.slice(1), 16);
        const r1 = (c1 >> 16) & 255;
        const g1 = (c1 >> 8) & 255;
        const b1 = c1 & 255;
        const r2 = (c2 >> 16) & 255;
        const g2 = (c2 >> 8) & 255;
        const b2 = c2 & 255;
        const r = Math.round(r1 + (r2 - r1) * factor);
        const g = Math.round(g1 + (g2 - g1) * factor);
        const b = Math.round(b1 + (b2 - b1) * factor);
        return `rgb(${r}, ${g}, ${b})`;
    };

    // Skeleton placeholder for pollen cards
    const SkeletonCard = () => (
        <div className={styles['env-card']} style={{ opacity: 0.6 }}>
            <div className={styles['env-icon']} style={{ background: 'var(--placeholder-bg)', borderRadius: '50%', width: 24, height: 24 }}></div>
            <div className={styles['env-info']}>
                <div className={styles['env-label']} style={{ background: 'var(--placeholder-bg)', height: '0.75rem', borderRadius: 4, width: '70%', marginBottom: '0.5rem' }}></div>
                <div className={styles['env-value']} style={{ background: 'var(--placeholder-bg)', height: '1.5rem', borderRadius: 4, width: '50%', marginBottom: '0.3rem' }}></div>
                <div style={{ fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <div style={{ background: 'var(--placeholder-bg)', height: '0.6rem', borderRadius: 4, width: '60%' }}></div>
                    <div style={{ background: 'var(--placeholder-bg)', height: '0.6rem', borderRadius: 4, width: '65%' }}></div>
                </div>
            </div>
        </div>
    );

    const CardManagerModal = () => {
        if (!showCardManager) return null;
        return (
            <div className={styles['modal-overlay']} onClick={() => setShowCardManager(false)}>
                <div className={`${styles['modal-content']} ${modalJustOpened ? styles['modal-slide-in'] : ''}`} onClick={e => e.stopPropagation()}>
                    <h3 className={styles['modal-title']}>Vis/Skjul kort</h3>
                    <div className={styles['card-toggle-list']}>
                        <label className={styles['card-toggle-item']}>
                            <input
                                type="checkbox"
                                checked={!isCardHidden(cardIds.weather)}
                                onChange={() => toggleCardVisibility(cardIds.weather)}
                            />
                            <span>Dagens forhold</span>
                        </label>
                        <label className={styles['card-toggle-item']}>
                            <input
                                type="checkbox"
                                checked={!isCardHidden(cardIds.tasks)}
                                onChange={() => toggleCardVisibility(cardIds.tasks)}
                            />
                            <span>I dag (Opgaver)</span>
                        </label>
                        <label className={styles['card-toggle-item']}>
                            <input
                                type="checkbox"
                                checked={!isCardHidden(cardIds.community)}
                                onChange={() => toggleCardVisibility(cardIds.community)}
                            />
                            <span>Community</span>
                        </label>
                        <label className={styles['card-toggle-item']}>
                            <input
                                type="checkbox"
                                checked={!isCardHidden(cardIds.inhaler)}
                                onChange={() => toggleCardVisibility(cardIds.inhaler)}
                            />
                            <span>Inhalator</span>
                        </label>
                        <label className={styles['card-toggle-item']}>
                            <input
                                type="checkbox"
                                checked={!isCardHidden(cardIds.aiChat)}
                                onChange={() => toggleCardVisibility(cardIds.aiChat)}
                            />
                            <span>AI Chat</span>
                        </label>
                        {/*
                        <label className={styles['card-toggle-item']}>
                            <input
                                type="checkbox"
                                checked={!isCardHidden(cardIds.health)}
                                onChange={() => toggleCardVisibility(cardIds.health)}
                            />
                            <span>Helbredsscore</span>
                        </label>
                        */}

                        <label className={styles['card-toggle-item']}>
                            <input
                                type="checkbox"
                                checked={!isCardHidden(cardIds.appointments)}
                                onChange={() => toggleCardVisibility(cardIds.appointments)}
                            />
                            <span>Kommende aftaler</span>

                        </label>

                        <label className={styles['card-toggle-item']}>
                            <input
                                type="checkbox"
                                checked={!isCardHidden(cardIds.articles)}
                                onChange={() => toggleCardVisibility(cardIds.articles)}
                            />
                            <span>Seneste artikler</span>
                        </label>

                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.dashboard}>
            {/* Daily summary bar at the top */}
            <div className={styles['dashboard-warning-bar']}>
                {dailySummaryLoading ? 'Henter dagens sundhedsopdatering...' : dailySummaryError ? dailySummaryError : dailySummary}
            </div>
            <CardManagerModal />
            <main className={styles['dashboard-main']}>
                <div className={styles['welcome-section']}>
                    <h1 className={styles['welcome-title']}>Velkommen tilbage</h1>
                    <p className={styles['welcome-subtitle']}>{currentDate}</p>
                    <button
                        className={`${styles['card-manager-btn']} ${recentlyClosedCard ? styles.pulseShimmer : ''}`}
                        onClick={() => setShowCardManager(!showCardManager)}
                        title="Administrer synlighed af kort"
                    >
                        <Settings size={20} />
                    </button>
                </div>

                <div className={styles['dashboard-grid']}>
                    <div className={styles['main-column']}>
                        {/* Kommende aftaler (API-driven) */}
                        {!isCardHidden(cardIds.appointments) && (
                            <div className={styles.card}>
                                <div className={styles['card-header-with-close']}>
                                    <div className={styles['card-header']}>
                                        <h2 className={styles['card-title']}><Stethoscope size={20} color="var(--text-secondary)" /> Kommende aftaler</h2>
                                    </div>
                                    <button
                                        className={styles['card-close-btn']}
                                        onClick={() => toggleCardVisibility(cardIds.appointments)}
                                        title="Skjul dette kort"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                                <div>
                                    {(() => {
                                        // Find all future tasks (today or later)
                                        const now = new Date();
                                        const futureTasks = tasks.filter(t => {
                                            const d = new Date(t.time || t.startAt || t.date || '');
                                            // Only show tasks that are not DONE or CANCELLED
                                            return d >= now && t.status !== 'DONE' && t.status !== 'CANCELLED';
                                        }).sort((a, b) => new Date(a.time || a.startAt || a.date) - new Date(b.time || b.startAt || b.date));
                                        if (loading) {
                                            return <div className={styles['no-events']}>Indlæser...</div>;
                                        }
                                        if (futureTasks.length === 0) {
                                            return <div className={styles['no-events']}>Ingen fremtidige opgaver endnu</div>;
                                        }
                                        // Helper for formatting date/time
                                        function formatAppointmentDate(dateObj) {
                                            const now = new Date();
                                            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                            const tomorrow = new Date(today);
                                            tomorrow.setDate(today.getDate() + 1);
                                            const d = new Date(dateObj);
                                            const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                                            const timeStr = d.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
                                            if (dDay.getTime() === today.getTime()) {
                                                return `I dag kl. ${timeStr}`;
                                            } else if (dDay.getTime() === tomorrow.getTime()) {
                                                return `I morgen kl. ${timeStr}`;
                                            } else {
                                                const monthText = d.toLocaleString('da-DK', { month: 'long' });
                                                return `d. ${d.getDate()} ${monthText} kl. ${timeStr}`;
                                            }
                                        }

                                        return futureTasks.slice(0, 3).map(task => {
                                            const d = new Date(task.time || task.startAt || task.date || '');
                                            const day = d.getDate();
                                            const month = d.toLocaleString('da-DK', { month: 'short' }).toUpperCase();
                                            return (
                                                <div key={task.id} className={styles['appointment-item']}>
                                                    <div className={styles['appointment-date']}>
                                                        <div className={styles['appointment-day']}>{day}</div>
                                                        <div className={styles['appointment-month']}>{month}</div>
                                                    </div>
                                                    <div className={styles['appointment-details']}>
                                                        <div className={styles['appointment-title']}>
                                                            {task.title}
                                                            {task.status === 'DONE' && (
                                                                <span style={{ color: 'green', marginLeft: 8 }} title="Udført">✔️</span>
                                                            )}
                                                        </div>
                                                        <div className={styles['appointment-time']}><Clock size={14} /> {formatAppointmentDate(d)}</div>
                                                    </div>
                                                    <div className={styles['task-actions']}>
                                                        <button className={styles['task-btn']} onClick={() => handleTaskComplete(task.id)}>Udført</button>
                                                        <button className={`${styles['task-btn']} ${styles.secondary}`} onClick={() => handleTaskSnooze(task.id)}>Udsæt 15 min</button>
                                                        <button className={`${styles['task-btn']} ${styles.tertiary}`} onClick={() => handleTaskCancel(task.id)}>Annuller</button>
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        )}
                        {/* Environmental Conditions */}
                        {!isCardHidden(cardIds.weather) && <div className={styles.card}>
                            <div className={styles['card-header-with-close']}>
                                <div className={styles['card-header']}>
                                    <h2 className={styles['card-title']}><Activity size={24} color="var(--text-secondary)" /> Dagens forhold</h2>
                                    <LocationButton
                                        onFetch={handleLocationClick}
                                        loading={weatherLoading}
                                        hidden={locationRequested}
                                    />
                                </div>
                                <button
                                    className={styles['card-close-btn']}
                                    onClick={() => toggleCardVisibility(cardIds.weather)}
                                    title="Skjul dette kort"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {weatherError ? (
                                <SmallError text={weatherError} />
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {isStale && (
                                        <div style={{ padding: '0.5rem', background: 'var(--dashboard-warning-bg)', border: '1px solid var(--dashboard-warning-border)', borderRadius: 6, color: 'var(--dashboard-warning-text)', fontSize: 13, textAlign: 'center' }}>
                                            ⚠️ Pollendata kan være forældet
                                        </div>
                                    )}

                                    <div className={styles['env-grid']}>
                                        {weatherLoading && !weatherData ? (
                                            <>
                                                <SkeletonCard />
                                                <SkeletonCard />
                                                <SkeletonCard />
                                                <SkeletonCard />
                                            </>
                                        ) : (
                                            <>
                                                <div className={styles['env-card']} style={{ borderColor: getPollenColor(average_pollen), borderWidth: '1px' }}>
                                                    <div className={styles['env-icon']}><Wind size={24} /></div>
                                                    <div className={styles['env-info']}>
                                                        <div className={styles['env-label']}>Gennemsnitlig pollen</div>
                                                        <div style={{ display: 'flex', gap: '2rem', marginTop: '0.3rem', alignItems: 'flex-start' }}>
                                                            <div className={styles['env-value']}>{average_pollen != null ? Math.round(average_pollen) : '—'}</div>
                                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', flex: 1 }}>
                                                                {birch_pollen != null && <div>Birk: {Math.round(birch_pollen)}</div>}
                                                                {grass_pollen != null && <div>Græs: {Math.round(grass_pollen)}</div>}
                                                                {mugwort_pollen != null && <div>Malurt: {Math.round(mugwort_pollen)}</div>}
                                                                {ragweed_pollen != null && <div>Ambrosie: {Math.round(ragweed_pollen)}</div>}
                                                                {alder_pollen != null && <div>El: {Math.round(alder_pollen)}</div>}
                                                                {olive_pollen != null && <div>Oliven: {Math.round(olive_pollen)}</div>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={styles['env-card']} style={{ borderColor: getHumidityColor(humidity), borderWidth: '1px' }}>
                                                    <div className={styles['env-icon']}><Droplets size={24} /></div>
                                                    <div className={styles['env-info']}>
                                                        <div className={styles['env-label']}>Luftfugtighed</div>
                                                        <div className={styles['env-value']}>{humidity ?? '—'}%</div>
                                                    </div>
                                                </div>

                                                <div className={styles['env-card']} style={{ borderColor: getUvColor(uv_index), borderWidth: '1px' }}>
                                                    <div className={styles['env-icon']}><TrendingUp size={24} /></div>
                                                    <div className={styles['env-info']}>
                                                        <div className={styles['env-label']}>UV-indeks</div>
                                                        <div className={styles['env-value']}>{uv_index != null ? Math.round(uv_index) : '—'}</div>
                                                    </div>
                                                </div>

                                                <div className={styles['env-card']} style={{ borderColor: getAqiColor(european_aqi), borderWidth: '1px' }}>
                                                    <div className={styles['env-icon']}><Activity size={24} /></div>
                                                    <div className={styles['env-info']}>
                                                        <div className={styles['env-label']}>Luftkvalitet</div>
                                                        <div className={styles['env-value']}>{european_aqi != null ? Math.round(european_aqi) : '—'}</div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>}

                        {/* Upcoming Events */}
                        {!isCardHidden(cardIds.tasks) && <div className={styles.card}>
                            <div className={styles['card-header-with-close']}>
                                <div className={styles['card-header']}>
                                    <h2 className={styles['card-title']}><Calendar size={24} color="var(--text-secondary)" />Kalender</h2>
                                    <Link to="/calendar" className={styles['card-action']}>Åben kalender</Link>
                                </div>
                                <button
                                    className={styles['card-close-btn']}
                                    onClick={() => toggleCardVisibility(cardIds.tasks)}
                                    title="Skjul dette kort"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {errors.tasks ? (
                                <SmallError text="Serverfejl: kunne ikke indlæse opgaver" />
                            ) : (
                                <>

                                    {/* week preview counts */}
                                    <div className={styles['week-preview']}>
                                        {(() => {
                                            const start = new Date();
                                            start.setHours(0, 0, 0, 0);
                                            const days = [];
                                            for (let i = 0; i < 7; i++) {
                                                const d = new Date(start);
                                                d.setDate(start.getDate() + i);
                                                const cnt = tasks.filter(t => {
                                                    const dt = new Date(t.time || t.startAt || t.date || '');
                                                    return dt.toDateString() === d.toDateString();
                                                }).length;
                                                days.push({ d, cnt });
                                            }
                                            return days.map(({ d, cnt }, idx) => (
                                                <div key={idx} className={styles['week-day']}>
                                                    <div className={styles['day-label']}>{d.toLocaleDateString('da-DK', { weekday: 'short' })}</div>
                                                    <div className={styles['day-count']}>{cnt}</div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </>
                            )}
                        </div>}

                        {/* Community */}
                        {!isCardHidden(cardIds.community) && <div className={styles.card}>
                            <div className={styles['card-header-with-close']}>
                                <div className={styles['card-header']}>
                                    <h2 className={styles['card-title']}><Users size={24} color="var(--text-secondary)" /> Community</h2>
                                    <Link to="/community" className={styles['card-action']}>Se alle <ChevronRight size={16} /></Link>
                                </div>
                                <button
                                    className={styles['card-close-btn']}
                                    onClick={() => toggleCardVisibility(cardIds.community)}
                                    title="Skjul dette kort"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {errors.posts ? (
                                <SmallError text="Serverfejl: kunne ikke indlæse community" />
                            ) : (
                                <>
                                    {communityPosts.slice(0, 3).map(post => (
                                        <div key={post.id} className={styles['community-post']}>
                                            <div className={styles['post-header']}>
                                                <div className={styles['post-avatar']}>{post.author ? post.author.split(' ').map(n => n[0]).join('') : 'U'}</div>
                                                <div className={styles['post-info']}>
                                                    <div className={styles['post-author']}>{post.author || 'Unknown'}</div>
                                                    <div className={styles['post-time']}>{post.time || '—'}</div>
                                                </div>
                                            </div>
                                            <div className={styles['post-content']}>{post.content || ''}</div>
                                            <div className={styles['post-footer']}>
                                                <div className={styles['post-stat']}><ThumbsUp size={14} /> {post.likes || 0}</div>
                                                <div className={styles['post-stat']}><MessageCircle size={14} /> {post.replies || 0} svar</div>
                                            </div>
                                        </div>
                                    ))}

                                    <button className={styles['card-action']} style={{ width: '100%', justifyContent: 'center', marginTop: '1rem' }}> <MessageSquare size={16} /> Stil et spørgsmål</button>
                                </>
                            )}
                        </div>}
                    </div>

                    {/* Sidebar */}
                    <div className={styles['sidebar-column']}>
                        {/* Removed 'Kommende aftaler' from sidebar, now in main column */}
                        {[1, 2, 3, 4, 5, 6].map(type => {
                            const dosesLeft = trackerStatus[type]?.dosesLeft ?? null;
                            return !isCardHidden(cardIds[type]) && (
                                <div className={styles.card} key={type}>
                                    <div className={styles['card-header-with-close']}>
                                        <div className={styles['card-header']}><h2 className={styles['card-title']}>{CONSUMABLE_TRACKER_TYPE_LABELS[type]}</h2></div>
                                        <button
                                            className={styles['card-close-btn']}
                                            onClick={() => toggleCardVisibility(cardIds[type])}
                                            title={`Skjul dette kort`}
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                    {errors.inhaler ? (
                                        <SmallError text={`Serverfejl: kunne ikke indlæse ${CONSUMABLE_TRACKER_TYPE_LABELS[type].toLowerCase()}`} />
                                    ) : (
                                        <div className={styles['inhaler-widget']}>
                                            <div className={styles['inhaler-circle']} onClick={() => handleTrackerUse(type)} style={{ cursor: 'pointer' }}>
                                                <svg width="160" height="160" style={{ transform: 'rotate(-90deg)' }}>
                                                    <circle cx="80" cy="80" r="70" className={styles['circle-bg']} />
                                                    <circle cx="80" cy="80" r="70" className={styles['circle-progress']} strokeDasharray={`${((dosesLeft || 0) / 120) * 440} 440`} style={{ stroke: getInhalerColor(dosesLeft) }} />
                                                </svg>
                                                <div className={styles['circle-text']}>
                                                    <span className={styles['circle-number']}>{dosesLeft ?? '—'}</span>
                                                    <span className={styles['circle-label']}>doser tilbage</span>
                                                </div>
                                            </div>
                                            <div className={styles['inhaler-actions']}>
                                                <button className={styles['inhaler-btn']} onClick={() => handleNewTracker(type)}>{`Ny ${CONSUMABLE_TRACKER_TYPE_LABELS[type]}`}</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {/*
                        {!isCardHidden(cardIds.health) && <div className={styles.card}>
                            <div className={styles['card-header-with-close']}>
                                <div className={styles['card-header']}><h2 className={styles['card-title']}>Helbredsscore</h2></div>
                                <button
                                    className={styles['card-close-btn']}
                                    onClick={() => toggleCardVisibility(cardIds.health)}
                                    title="Skjul dette kort"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <div className={styles['health-score-widget']}>
                                <div className={styles['score-circle']}><div><span className={styles['score-number']}>{healthScore ?? '—'}</span><span className={styles['score-max']}>/100</span></div></div>
                                <div className={styles['score-label']}>God</div>
                            </div>
                        </div>}
                        */}

                        {/* (moved to main column above) */}
                        {!isCardHidden(cardIds.aiChat) && (
                            <div className={styles.card}>
                                <div className={styles['card-header-with-close']}>
                                    <div className={styles['card-header']}>
                                        <h2 className={styles['card-title']}>
                                            AI Chat
                                        </h2>
                                    </div>
                                    <button
                                        className={styles['card-close-btn']}
                                        onClick={() => toggleCardVisibility(cardIds.aiChat)}
                                        title="Skjul dette kort"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>

                                <AIChatWidget />
                            </div>
                        )}
                        {!isCardHidden(cardIds.articles) && (
                            <div className={styles.card}>
                                <div className={styles['card-header-with-close']}>
                                    <div className={styles['card-header']}>
                                        <h2 className={styles['card-title']}>
                                            <BookOpen size={20} color="var(--text-secondary)" />
                                            <span style={{ cursor: 'pointer' }} onClick={() => window.location.href = '/articles'}> Seneste artikler</span>
                                        </h2>
                                    </div>
                                    <button
                                        className={styles['card-close-btn']}
                                        onClick={() => toggleCardVisibility(cardIds.articles)}
                                        title="Skjul dette kort"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                                {errors.articles ? (
                                    <SmallError text="Serverfejl: kunne ikke indlæse artikler" />
                                ) : (
                                    <div>
                                        <ArticlesPreview articles={articles.slice(0, 3)} />
                                        <button className={styles['card-action']} style={{ width: '100%', justifyContent: 'center', marginTop: '1rem' }} onClick={() => window.location.href = '/articles'}>
                                            Gå til artikler
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
